import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Circle from '@mui/icons-material/Circle';
import PlayArrowOutlined from '@mui/icons-material/PlayArrowOutlined';
import PowerSettingsNewOutlined from '@mui/icons-material/PowerSettingsNewOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useHostingAdvancedServer } from '@newfold/huapi-js';

import { useAlert } from '~/components/Alerts/alertsStore';
import ButtonAlt from '~/components/ButtonAlt';
import SSOLink from '~/components/SSOLink';
import useAccount from '~/hooks/useAccount';
import useAMRevamp from '~/hooks/useAMRevamp';
import {
  productTypes,
  useCtbProductInfo,
  useCtbPurchaseStatusQuery,
} from '~/hooks/useCtbProductInfo';
import useFeature from '~/hooks/useFeature';
import useServerStatusToggle from '~/hooks/useServerStatusToggle';
import ServerActionButtons from '~/Nova/scenes/HostingList/components/ServerActionButtons';
import { getServerStatusandColor } from '~/Nova/scenes/HostingList/utils/helpers';

import { DISABLE_SERVER_STATUS_BUTTON_STATUSES } from '../utils/constant';
import AdvancedHostingPowerOffServerModal from './PowerOffServerModal';
import AdvancedHostingRebootModal from './RebootModal';
import AdvancedHostingServerStatusModal from './ServerStatusModal';

interface ServerStatusInputs {
  openRebootModal: boolean;
  openPowerOffServerModal: boolean;
  showRebootSuccessfullTxt: boolean;
  showPowerOffSuccessfullTxt: boolean;
  showServerOnSuccessfullTxt: boolean;
  openServerStatusSuccessModal: boolean;
  showLoaderForRebootServer: boolean;
  showLoaderForStartServer: boolean;
  showLoaderForPowerOffServer: boolean;
}

interface AdvancedHostingServerStatusProps {
  handleServerStatusChange?: (value: string) => void; // optional callback
  planHostingId?: string;
  buttonGap?: number;
  serverType?: string;
}

const AdvancedHostingServerStatus = ({
  handleServerStatusChange = undefined,
  planHostingId = '',
  buttonGap = 4,
  serverType = '',
}: AdvancedHostingServerStatusProps) => {
  const { t } = useTranslation('settings', {
    keyPrefix: 'nameServerAndIPCard',
  });

  const generateAlert = useAlert();
  const { id } = useAccount();
  const hostingId = planHostingId || id;
  const { isAMRevamp } = useAMRevamp(); // Extract the value of isAMRevamp

  const [hasAdvancedHosting] = useFeature('advanced_hosting', hostingId);
  const [isPlesk] = useFeature('plesk', hostingId);
  const [isWindows] = useFeature('windows', hostingId);
  const [showServerControls, , clientReady] = useFeature(
    'server_controls',
    hostingId,
  );

  const { start, stop, reboot } = useServerStatusToggle({ hostingId });

  const [inputs, setInputsState] = useState<ServerStatusInputs>({
    openRebootModal: false,
    openPowerOffServerModal: false,
    showRebootSuccessfullTxt: false,
    showPowerOffSuccessfullTxt: false,
    showServerOnSuccessfullTxt: false,
    openServerStatusSuccessModal: false,
    showLoaderForRebootServer: false,
    showLoaderForStartServer: false,
    showLoaderForPowerOffServer: false,
  });

  const setAlertMessage = useCallback(
    (variant: string, descKey: string) => {
      generateAlert({
        severity: variant,
        description: descKey,
        showCloseBtn: true,
      });
    },
    [generateAlert],
  );

  const handleInputs = (target: { name: any; value: any }) => {
    // destructuring the object
    let { name, value } = target;
    setInputsState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const systemConsoleData: any = {
    name: 'systemconsole',
    type: 'vps',
    canShow: hasAdvancedHosting && !isPlesk && !isWindows,
    queryParams: {
      app: 'systemconsole',
    },
  };

  const refetchInterval = 5000;

  //CTB upgrade status
  const productInfo = useCtbProductInfo(productTypes.HOSTING_UPGRADE);
  const { hasPurchasedRecently } = useCtbPurchaseStatusQuery(
    productInfo.purchaseQueryKey,
  );

  const intermediateStates = [
    'start',
    'stop',
    'pending_stop',
    'reboot_soft',
    'pending_reboot_soft',
    'pending_resize',
    'resize',
    'pending_start',
  ];
  const finalStates = useMemo(() => ['active', 'stopped'], []);

  //Get Server Status
  const {
    data,
    isPending: isPendingServerStatus,
    isSuccess: isSuccessServerStatus,
    isError: isErrorServerStatus,
    refetch: refetchServerStatus,
  } = useHostingAdvancedServer(hostingId, {
    query: {
      enabled: !!hostingId && clientReady && showServerControls,
      refetchInterval: (query) => {
        const status = query?.state?.data?.data?.status!;
        return !!hostingId &&
          clientReady &&
          showServerControls &&
          intermediateStates.includes(status)
          ? refetchInterval
          : false;
      },
    },
  });

  // onSuccess - useHostingAdvancedServer
  useEffect(() => {
    if (isSuccessServerStatus) {
      if (
        !isPendingServerStatus &&
        showServerControls &&
        finalStates.includes(data?.data?.status!) &&
        (inputs.showLoaderForStartServer ||
          inputs.showLoaderForRebootServer ||
          inputs.showLoaderForPowerOffServer)
      ) {
        handleInputs({ name: 'showLoaderForRebootServer', value: false });
        handleInputs({ name: 'showLoaderForStartServer', value: false });
        handleInputs({ name: 'showLoaderForPowerOffServer', value: false });
        handleInputs({ name: 'openServerStatusSuccessModal', value: true });
      }
      if (typeof handleServerStatusChange === 'function') {
        handleServerStatusChange(data?.data?.status!);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSuccessServerStatus]);

  // onError - useHostingAdvancedServer
  useEffect(() => {
    if (isErrorServerStatus) {
      setAlertMessage('error', t('advHostingServerInfo.errorMsg'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorServerStatus]);

  useEffect(() => {
    if (showServerControls && hasPurchasedRecently === false) {
      refetchServerStatus();
    }
  }, [showServerControls, hasPurchasedRecently, refetchServerStatus]);

  const serverStatus = data?.data.status!;
  const isServerStatusQueryEnabled = !!hostingId && showServerControls;
  //Treating most of the case as loading to avoid flickering of the UI
  const isServerStatusLoading =
    isServerStatusQueryEnabled &&
    !isSuccessServerStatus &&
    !isErrorServerStatus;

  const disableServerStatusButtons =
    isServerStatusLoading ||
    [...DISABLE_SERVER_STATUS_BUTTON_STATUSES, 'available'].includes(
      serverStatus,
    );

  const disableStartServerStatusButton =
    isServerStatusLoading ||
    [...DISABLE_SERVER_STATUS_BUTTON_STATUSES, 'active'].includes(serverStatus);

  const disableStopServerStatusButton =
    isServerStatusLoading ||
    [...DISABLE_SERVER_STATUS_BUTTON_STATUSES, 'stopped'].includes(
      serverStatus,
    );

  const handleServerStatus = async () => {
    const request = serverStatus === 'active' ? stop() : start();

    if (request === 'start') {
      handleInputs({ name: 'showLoaderForStartServer', value: true });
      handleInputs({ name: 'showServerOnSuccessfullTxt', value: true });
    } else {
      handleInputs({ name: 'openPowerOffServerModal', value: false });
      handleInputs({ name: 'showLoaderForPowerOffServer', value: true });
    }
  };

  //RebootButton
  const handleRebootServer = async () => {
    reboot();

    handleInputs({ name: 'openRebootModal', value: false });
    handleInputs({ name: 'showLoaderForRebootServer', value: true });
  };

  const showRebootServerModal = () => {
    handleInputs({ name: 'openRebootModal', value: true });
    handleInputs({ name: 'showRebootSuccessfullTxt', value: true });
  };

  const showPowerOffServerModal = () => {
    handleInputs({ name: 'openPowerOffServerModal', value: true });
    handleInputs({ name: 'showPowerOffSuccessfullTxt', value: true });
  };

  const handleClose = () => {
    handleInputs({ name: 'showPowerOffSuccessfullTxt', value: false });
    handleInputs({ name: 'showRebootSuccessfullTxt', value: false });
    handleInputs({ name: 'openRebootModal', value: false });
    handleInputs({ name: 'openPowerOffServerModal', value: false });
  };

  const handleSuccessfulModalClose = () => {
    handleInputs({ name: 'showRebootSuccessfullTxt', value: false });
    handleInputs({ name: 'showPowerOffSuccessfullTxt', value: false });
    handleInputs({ name: 'showServerOnSuccessfullTxt', value: false });
    handleInputs({ name: 'openServerStatusSuccessModal', value: false });
  };
  const { color, status } = getServerStatusandColor(serverStatus);
  // server status color
  const statusColor = isErrorServerStatus ? 'error.main' : color;

  // Render ServerActionButtons when isAMRevamp is true
  const renderServerActionButtons = isAMRevamp ? (
    <ServerActionButtons
      serverAddonStatus={hasPurchasedRecently || isErrorServerStatus}
      disableStartServer={disableStartServerStatusButton}
      disableRebootServer={disableServerStatusButtons}
      disableStopServer={disableStopServerStatusButton}
      onStartClick={handleServerStatus}
      onRebootClick={showRebootServerModal} // <- fix: show modal
      onStopClick={showPowerOffServerModal}
      systemConsoleData={systemConsoleData}
      hostingId={hostingId}
      buttonGap={buttonGap}
      serverType={serverType}
      serverStatusLoading={isServerStatusLoading}
    />
  ) : (
    <>
      <Stack spacing={2}>
        <Typography variant="h2">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Circle sx={{ fontSize: 10, color: statusColor }} />
            <span>
              {t('advHostingServerInfo.serverTxt')}
              <Typography variant="h2" color={statusColor} component={'span'}>
                &nbsp;
                {status}
              </Typography>
            </span>
          </Stack>
        </Typography>

        <Stack
          gap={3}
          direction={'row'}
          flexWrap="wrap"
          justifyItems={'stretch'}
        >
          <ButtonAlt
            aria-label="StartServer"
            data-testid="start-server-button"
            disabled={
              disableStartServerStatusButton ||
              hasPurchasedRecently ||
              isErrorServerStatus
            }
            onClick={handleServerStatus}
          >
            <Stack alignItems="center" spacing={1}>
              {inputs.showLoaderForStartServer ? (
                <CircularProgress size={24} />
              ) : (
                <PlayArrowOutlined sx={{ fontSize: 24 }} />
              )}
              <Typography variant="body2">
                {t('advHostingServerInfo.startServer')}
              </Typography>
            </Stack>
          </ButtonAlt>

          <ButtonAlt
            aria-label="RebootServer"
            onClick={showRebootServerModal}
            disabled={
              disableServerStatusButtons ||
              hasPurchasedRecently ||
              isErrorServerStatus
            }
          >
            <Stack alignItems="center" spacing={1}>
              {inputs.showLoaderForRebootServer ? (
                <CircularProgress size={24} />
              ) : (
                <RefreshIcon color="action" sx={{ fontSize: 24 }} />
              )}
              <Typography variant="body2">
                {t('advHostingServerInfo.rebootServer')}
              </Typography>
            </Stack>
          </ButtonAlt>

          <ButtonAlt
            aria-label="StopServer"
            onClick={showPowerOffServerModal}
            disabled={
              disableStopServerStatusButton ||
              hasPurchasedRecently ||
              isErrorServerStatus
            }
          >
            <Stack alignItems="center" spacing={1}>
              {inputs.showLoaderForPowerOffServer ? (
                <CircularProgress size={24} />
              ) : (
                <PowerSettingsNewOutlined
                  color="action"
                  sx={{ fontSize: 24 }}
                />
              )}

              <Typography variant="body2">
                {t('advHostingServerInfo.powerOffServer')}
              </Typography>
            </Stack>
          </ButtonAlt>

          {systemConsoleData?.canShow && (
            <SSOLink
              type={systemConsoleData.type}
              queryParams={systemConsoleData.queryParams}
              variant="manageConsoleIconButton"
            ></SSOLink>
          )}
        </Stack>
      </Stack>
    </>
  );
  return (
    <>
      {renderServerActionButtons}

      <AdvancedHostingRebootModal
        openRebootModal={inputs.openRebootModal}
        handleClose={handleClose}
        handleRebootServer={handleRebootServer}
      />
      <AdvancedHostingPowerOffServerModal
        openPowerOffServerModal={inputs.openPowerOffServerModal}
        handleServerStatus={handleServerStatus}
        handleClose={handleClose}
      />
      <AdvancedHostingServerStatusModal
        openServerStatusSuccessModal={inputs.openServerStatusSuccessModal}
        handleSuccessfullModalClose={handleSuccessfulModalClose}
        showRebootSuccessfullTxt={inputs.showRebootSuccessfullTxt}
        showPowerOffSuccessfullTxt={inputs.showPowerOffSuccessfullTxt}
        showServerOnSuccessfullTxt={inputs.showServerOnSuccessfullTxt}
      />
    </>
  );
};

export default AdvancedHostingServerStatus;
